import TooltipEllipsis from '@deltek/specification-client/core/Feedbacks/TooltipEllipsis'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Button from '@material-ui/core/Button'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import { SearchFacets } from 'api/graphQL/types/product.type'
import clsx from 'clsx'
import { getPTAndFamilyFromString } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React from 'react'
import ReactResizeDetector from 'react-resize-detector'
import {
    advancedFilterDefaultColor,
    listingDialogBreadCrumbContainerColor,
    listingDialogFilterContainerColor,
} from 'shared/common/productListingDialogColors'
import {
    removeDuplicateProductTypeFacets,
    WORKSPACE_PRODUCT_LISTING_KEY,
    WorkspaceProductListing,
} from 'shared/utils/workspaceUtils'
import { removeFamilyNumberFromFamilyNameRegex } from '../Card/ProductListingCard'
import { DialogContext } from '../DialogContext'
import { DialogFilterContext } from '../DialogFilterContext'
import { ProductListingDialogFacet } from '../ProductListingDialog.types'
import { DialogFilter } from './DialogFilter'
const useStyles = makeStyles((theme: Theme) => ({
    productHeader: {
        padding: theme.spacing(1, 3),
        backgroundColor: listingDialogFilterContainerColor,
    },
    filterBox: {
        flex: 1,
        width: '100%',
    },
    formRoot: {
        '& legend span': {
            fontSize: '0.75em',
        },
    },
    formsLabel: {
        fontSize: pxToRem(12),
        fontWeight: 400,
    },
    formControl: {
        minWidth: 120,
    },
    formControlLabel: {
        fontSize: pxToRem(12),
        fontWeight: 400,
        color: '#464646',
        lineHeight: 1.5,
    },
    headerCategory: {
        color: '#304959',
        fontWeight: 500,
        paddingLeft: theme.spacing(3),
    },
    contextButtonsHeader: {
        backgroundColor: listingDialogBreadCrumbContainerColor,
        padding: theme.spacing(2, 3),
    },
    buttonCommon: {
        borderRadius: theme.spacing(2.5),
        fontSize: pxToRem(16),
        lineHeight: 1.43,
        padding: '5px 15px',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
    },
    gridItemWorkspaceContext: {
        flex: '1 1 100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        overflowX: 'hidden',
    },
    tooltipWorkspaceContext: {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    buttonActive: {
        color: advancedFilterDefaultColor,
        backgroundColor: theme.palette.common.white,
        '&:hover': {
            backgroundColor: listingDialogFilterContainerColor,
        },
    },
    buttonInactive: {
        color: theme.palette.common.white,
        borderColor: theme.palette.common.white,
    },
}))

interface DialogWorkspaceHeaderProps {
    category: string
    handleAutocompleteInputValueChange: (
        facet: ProductListingDialogFacet,
        value: string
    ) => void
    productTypeFacets: Partial<SearchFacets>[]
    handleAutocompleteNameChange: (facet?: ProductListingDialogFacet) => void
    autosuggestLoading?: boolean
    queryParams?: URLSearchParams
}

export const DialogWorkspaceHeader: React.FC<DialogWorkspaceHeaderProps> = ({
    category,
    productTypeFacets,
    handleAutocompleteInputValueChange,
    handleAutocompleteNameChange,
    autosuggestLoading,
    queryParams,
}) => {
    const serviceWorker = navigator.serviceWorker
    const [localPTInput, setLocalPT] = React.useState<string>('')
    const [categoryTootipWidth, setCategoryTootipWidth] = React.useState(0)
    const {
        slideOpen,
        handleSlideOpen,
        isAdvancedFiltersOpen,
        handleAdvancedFiltersOpen,
        handleHeaderPillToggle,
        isAEAllProductsToggled,
        isProductsPage,
        projectDetail,
        isPTNotSelectedInFilter,
        isFamilyNotSelectedInFilter,
        getFacetSelectedOptions,
    } = React.useContext(DialogContext)
    // this determines if project is from SMM workspace (DraftProject)
    // flag used in ProductListingCard
    const isDraftProject =
        projectDetail?.number === 'DraftProject' ||
        projectDetail?.groupName === null
    const classes = useStyles()
    const elementType = queryParams?.get('elementType')

    const {
        handleFacetSelectedChange,
        getFacetOptions,
        getProductFacetOptions,
        isProductTypeFacetsLoading,
    } = React.useContext(DialogFilterContext)
    const handleManufacturerOpen = React.useCallback(() => {
        handleAutocompleteNameChange('FirmName')
    }, [handleAutocompleteNameChange])

    const handleManufacturerInputChange = React.useCallback(
        (value: string) => {
            handleAutocompleteInputValueChange('FirmName', value)
        },
        [handleAutocompleteInputValueChange]
    )

    const handleFamilyOpen = React.useCallback(() => {
        handleAutocompleteNameChange('SectionNames')
    }, [handleAutocompleteNameChange])

    const handleProductTypeOpen = React.useCallback(() => {
        handleAutocompleteNameChange('ProductType')
    }, [handleAutocompleteNameChange])

    const handleFamilyInputChange = React.useCallback(
        (value: string) => {
            setLocalPT(value)
            handleAutocompleteInputValueChange('SectionNames', value)
        },
        [handleAutocompleteInputValueChange]
    )

    const handleProductTypeInputChange = React.useCallback(
        (value: string) => {
            setLocalPT(value)
            handleAutocompleteInputValueChange('ProductType', value)
        },
        [handleAutocompleteInputValueChange]
    )
    const handleClose = React.useCallback(() => {
        handleAutocompleteNameChange()
    }, [handleAutocompleteNameChange])

    const getValue = React.useCallback(
        (value: ProductListingDialogFacet) => {
            return getFacetSelectedOptions(value)
        },
        [getFacetSelectedOptions]
    )

    const getOption = React.useCallback(
        (value: ProductListingDialogFacet) => {
            return getFacetOptions(value)
        },
        [getFacetOptions]
    )
    const getProductOptions = React.useCallback(
        (value: ProductListingDialogFacet) => {
            return getProductFacetOptions?.(value)
        },
        [getProductFacetOptions]
    )

    const handleSelectedValue = (
        facet: ProductListingDialogFacet,
        value: string[],
        productValue?: Partial<SearchFacets>[]
    ) => {
        if (facet === 'ProductType' && productValue) {
            const { ptNames, familyNames } = getPTAndFamilyFromString(value)
            const updatedFamilyInput = removeDuplicateProductTypeFacets([
                ...productValue,
                ...productTypeFacets,
            ])
                ?.filter((item) =>
                    familyNames.some(
                        (itemValue) =>
                            itemValue.trim() ===
                            item.parentProductFamily?.trim()
                    )
                )
                .reduce((prev: Partial<SearchFacets>[], current) => {
                    const isExist = prev.find(
                        (item) =>
                            item.value === current.value &&
                            item.parentProductFamily ===
                            current.parentProductFamily
                    )
                    if (!isExist) {
                        return prev.concat([current])
                    } else {
                        return prev
                    }
                }, [])

            if (getValue('SectionNames').length === 0) {
                const updatedFamilyNames = familyNames?.reduce(
                    (prev: string[], current) => {
                        const isExist = prev.find((item) => item === current)
                        if (!isExist) {
                            return prev.concat([current])
                        } else {
                            return prev
                        }
                    },
                    []
                )
                handleFacetSelectedChange(
                    'SectionNames',
                    updatedFamilyNames,
                    updatedFamilyInput,
                    localPTInput.length !== 0 ? localPTInput : undefined
                )
            }
            handleFacetSelectedChange(
                'ProductType',
                ptNames as string[],
                updatedFamilyInput
            )
        } else if (facet === 'SectionNames' && productValue) {
            const newSelectedProductType = getValue('ProductType').filter((i) =>
                value.some((val) => {
                    const familyName = i.split('||').pop()
                    return familyName?.trim() === val
                })
            )
            const { ptNames } = getPTAndFamilyFromString(newSelectedProductType)
            handleFacetSelectedChange(
                'SectionNames',
                value,
                productValue,
                localPTInput.length !== 0 ? localPTInput : undefined
            )
            handleFacetSelectedChange('ProductType', ptNames as string[])
        } else {
            handleFacetSelectedChange(facet, value as string[])
        }
    }

    // Read data from service worker
    React.useEffect(() => {
        if (!serviceWorker) return

        const handleServiceWorkerMessage = ({ data }: MessageEvent) => {
            // make sure that the data coming from SW is for this page.
            const workspaceProductListingState = data[
                WORKSPACE_PRODUCT_LISTING_KEY
            ] as WorkspaceProductListing
        }

        serviceWorker?.addEventListener('message', handleServiceWorkerMessage)
        return () => {
            serviceWorker?.removeEventListener(
                'message',
                handleServiceWorkerMessage
            )
        }
    }, [serviceWorker, elementType])

    const handleToggleSlideOut = (
        event: React.ChangeEvent<HTMLInputElement>,
        state: boolean
    ) => {
        handleAdvancedFiltersOpen?.(state)
        handleSlideOpen?.(false)
    }

    const handleAllProductsToggle = React.useCallback(
        (isToggled: boolean) => () => {
            handleHeaderPillToggle?.(isToggled)
        },
        [handleHeaderPillToggle]
    )

    return (
        <>
            {!isProductsPage && !isDraftProject && !!category && (
                <GridContainer
                    direction="row"
                    className={classes.contextButtonsHeader}
                    spacing={1}
                    wrap="nowrap"
                >
                    <GridItem>
                        <Button
                            variant="outlined"
                            onClick={handleAllProductsToggle(true)}
                            className={clsx(
                                classes.buttonCommon,
                                !!isAEAllProductsToggled
                                    ? classes.buttonActive
                                    : classes.buttonInactive
                            )}
                        >
                            All Products
                        </Button>
                    </GridItem>
                    <GridItem className={classes.gridItemWorkspaceContext}>
                        <Button
                            variant="outlined"
                            onClick={handleAllProductsToggle(false)}
                            className={clsx(
                                classes.buttonCommon,
                                !isAEAllProductsToggled
                                    ? classes.buttonActive
                                    : classes.buttonInactive
                            )}
                        >
                            <TooltipEllipsis
                                lineLimit={1}
                                text={category}
                                width={categoryTootipWidth}
                                className={classes.tooltipWorkspaceContext}
                            >
                                {category}
                            </TooltipEllipsis>
                        </Button>
                        <ReactResizeDetector
                            handleHeight={false}
                            handleWidth={true}
                            onResize={(width: number) =>
                                setCategoryTootipWidth(
                                    width > 35 ? width - 35 : width
                                )
                            }
                        />
                    </GridItem>
                </GridContainer>
            )}
            <GridContainer
                direction="row"
                className={classes.productHeader}
                alignItems="center"
                spacing={1}
            >
                <GridItem className={classes.filterBox}>
                    <GridContainer direction="row" item spacing={1}>
                        <GridItem
                            xs={12}
                            sm={6}
                            md={slideOpen || isAdvancedFiltersOpen ? 6 : 4}
                            lg={4}
                        >
                            <DialogFilter
                                name="families"
                                label="Families"
                                value={getValue('SectionNames').filter(
                                    (item, pos) =>
                                        getValue('SectionNames').indexOf(
                                            item
                                        ) === pos
                                )}
                                options={getOption('SectionNames')}
                                productTypeOptions={getProductOptions(
                                    'SectionNames'
                                )}
                                handleValue={(value, productValue) =>
                                    value
                                        ? handleSelectedValue(
                                            'SectionNames',
                                            value as string[],
                                            productValue
                                        )
                                        : []
                                }
                                handleOpen={handleFamilyOpen}
                                handleClose={handleClose}
                                handleInput={handleFamilyInputChange}
                                loading={autosuggestLoading}
                                selectedDisplayItems={getValue(
                                    'SectionNames'
                                )?.map((x) => {
                                    return x.replace('|', ' - ')
                                })}
                                type="multiple"
                                fullWidth={!slideOpen && !isAdvancedFiltersOpen}
                                disabled={isProductTypeFacetsLoading}
                                isInputError={isFamilyNotSelectedInFilter}
                            />
                        </GridItem>

                        <GridItem
                            xs={12}
                            sm={6}
                            md={slideOpen || isAdvancedFiltersOpen ? 6 : 4}
                            lg={4}
                        >
                            <DialogFilter
                                name="productTypes"
                                label="Product Types"
                                value={getValue('ProductType')}
                                options={getOption('ProductType')}
                                productTypeOptions={getProductOptions(
                                    'ProductType'
                                )}
                                handleValue={(value, productValue) =>
                                    value
                                        ? handleSelectedValue(
                                            'ProductType',
                                            value as string[],
                                            productValue
                                        )
                                        : []
                                }
                                handleOpen={handleProductTypeOpen}
                                handleClose={handleClose}
                                handleInput={handleProductTypeInputChange}
                                loading={autosuggestLoading}
                                selectedDisplayItems={getValue('ProductType')}
                                type="multiple"
                                fullWidth={!slideOpen && !isAdvancedFiltersOpen}
                                disabled={isProductTypeFacetsLoading}
                                isInputError={isPTNotSelectedInFilter}
                            />
                        </GridItem>

                        <GridItem
                            xs={12}
                            sm={6}
                            md={slideOpen || isAdvancedFiltersOpen ? 6 : 4}
                            lg={4}
                        >
                            <DialogFilter
                                name="manufacturers"
                                label="Manufacturers"
                                value={getValue('FirmName')}
                                options={getOption('FirmName')}
                                handleValue={(value) =>
                                    value
                                        ? handleSelectedValue(
                                            'FirmName',
                                            value as string[]
                                        )
                                        : []
                                }
                                handleOpen={handleManufacturerOpen}
                                handleClose={handleClose}
                                handleInput={handleManufacturerInputChange}
                                selectedDisplayItems={getValue(
                                    'FirmName'
                                )?.map((x) =>
                                    x.replace(
                                        removeFamilyNumberFromFamilyNameRegex,
                                        ''
                                    )
                                )}
                                loading={autosuggestLoading}
                                type="multiple"
                                fullWidth={!slideOpen && !isAdvancedFiltersOpen}
                                disabled={isProductTypeFacetsLoading}
                            />
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>

            {!isProductsPage && !!isDraftProject && !!category && (
                <Typography
                    variant="h6"
                    className={clsx('lblCategory', classes.headerCategory)}
                >
                    {category}
                </Typography>
            )}
        </>
    )
}
